import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Scissors, Loader2, Check, Minus, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getUserToken, authHeaders } from "@/utils/auth";
import { subscribeTopInset } from "@/utils/telegram";
import { FaceTexture } from "@/components/FaceTexture";
import {
  PACK_PRICE, pluralTryons, planFeatures, rise,
  btnGoldSolid, btnGhost,
} from "@/components/accountUi";

import { API } from "../utils/api";

export default function AccountPlansPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paying, setPaying] = useState("");
  const [packQty, setPackQty] = useState(5);
  const [topInset, setTopInset] = useState(0);

  // Отступ под системный оверлей Telegram fullscreen — иначе шапка прячется под ним
  useEffect(() => subscribeTopInset(setTopInset), []);

  useEffect(() => {
    if (!getUserToken()) {
      navigate("/account", { replace: true });
      return;
    }
    axios.get(`${API}/plans`).then(({ data }) => setPlans(data)).catch(() => {});
    axios
      .get(`${API}/account/me`, { headers: authHeaders() })
      .then(({ data }) => setMe(data))
      .catch(() => navigate("/account", { replace: true }));
  }, []);

  const errText = (e) =>
    typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Что-то пошло не так. Попробуйте ещё раз.";

  // CloudPayments: браузерный onSuccess — только UX, источник истины — webhook на бэкенде.
  // Поэтому после оплаты опрашиваем статус заказа, пока webhook не активирует тариф.
  const pollPayment = async (orderId) => {
    for (let i = 0; i < 20; i += 1) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/payments/${orderId}`, { headers: authHeaders() });
        if (data.status === "succeeded") return true;
      } catch { /* повторяем */ }
    }
    return false;
  };

  const openWidget = (order, kind = "plan") => {
    if (!window.cp?.CloudPayments) {
      toast.error("Платёжная форма не загрузилась. Отключите блокировщик рекламы и обновите страницу.");
      setPaying("");
      return;
    }
    const widget = new window.cp.CloudPayments();
    widget.pay(
      "charge",
      {
        publicId: process.env.REACT_APP_CLOUDPAYMENTS_PUBLIC_ID,
        description: order.description,
        amount: order.amount,
        currency: "RUB",
        invoiceId: order.order_id,
        accountId: me?.id || order.order_id,
        skin: "mini",
      },
      {
        onSuccess: async () => {
          toast.success("Оплата прошла — активируем доступ…");
          const ok = await pollPayment(order.order_id);
          if (ok) {
            toast.success(kind === "pack"
              ? `Готово! Примерки на счёте (+${packQty})`
              : "Готово! Доступ активирован.");
            try {
              const { data } = await axios.get(`${API}/account/me`, { headers: authHeaders() });
              setMe(data);
            } catch { /* кабинет обновится при следующем заходе */ }
          } else {
            toast("Платёж обрабатывается", {
              description: "Обычно это занимает до минуты — доступ появится автоматически.",
            });
          }
        },
        onFail: () => {
          toast.error("Оплата не прошла. Попробуйте ещё раз или другую карту.");
          // Страховка: хук Fail в кабинете CloudPayments может быть не настроен —
          // сообщаем об отказе сами, чтобы владелец получил уведомление в Telegram
          axios.post(`${API}/payments/client-failed`, { order_id: order.order_id }).catch(() => {});
        },
        onComplete: () => setPaying(""),
      }
    );
  };

  const pay = async (plan) => {
    if (paying) return;
    setPaying(plan.id);
    try {
      const { data } = await axios.post(
        `${API}/payments/create`,
        { plan_id: plan.id },
        { headers: authHeaders() }
      );
      openWidget(data);
    } catch (e) {
      toast.error(errText(e));
      setPaying("");
    }
  };

  const buyPack = async () => {
    if (paying) return;
    setPaying("pack");
    try {
      const { data } = await axios.post(
        `${API}/payments/create-pack`,
        { qty: packQty },
        { headers: authHeaders() }
      );
      openWidget(data, "pack");
    } catch (e) {
      toast.error(errText(e));
      setPaying("");
    }
  };

  const sub = me?.subscription;
  const fmtUntil = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("ru-RU");
    } catch {
      return "";
    }
  };
  // Даунгрейд запрещён: при активном «Про» тарифы «Старт» недоступны (бэкенд тоже отклоняет — 409)
  const isDowngrade = (p) => Boolean(sub?.active && sub?.is_pro && !p.is_pro);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F4F0] selection:bg-[#C2A878]/30" data-testid="tariffs-page">
      <FaceTexture />
      <header
        className="fixed inset-x-0 top-0 z-40 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl"
        style={{ top: topInset }}
      >
        <div className="flex items-center justify-between px-6 py-4 md:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="tariffs-logo">
            <Scissors size={15} strokeWidth={1.5} className="text-[#C2A878]" />
            <span className="font-jetbrains text-sm tracking-[0.35em] text-[#F4F4F0]">AFANASIEV</span>
          </Link>
          <Link
            to="/account"
            className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
            data-testid="tariffs-back-link"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            В кабинет
          </Link>
        </div>
      </header>

      <main
        className="relative mx-auto max-w-md overflow-hidden px-6 pb-24 pt-28 md:max-w-lg"
        style={{ paddingTop: 112 + topInset }}
      >
        <motion.div {...rise(0)}>
          <p className="font-jetbrains text-[10px] uppercase tracking-[0.35em] text-[#C2A878]">( Тарифы )</p>
          <h1 className="mt-5 font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0]">
            Полная сила приложения — <span className="italic text-[#C2A878]">по тарифу</span>
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-[#A3A3A3]">
            Примерка новой стрижки на вашем лице, сохранение результатов и —
            в тарифах «Про» — технологическая карта для мастера: по ней стрижку
            повторят один в один.
          </p>
          <p className="mt-4 border border-white/10 p-4 text-xs leading-relaxed text-[#A3A3A3]">
            <span className="text-[#C2A878]">Что входит бесплатно:</span> точный разбор лица по 68 точкам,
            оценка текущей стрижки, 9 рекомендаций и все маски.{" "}
            <span className="text-[#C2A878]">Одна примерка</span> = одна генерация новой стрижки на вашем фото;
            она списывается только после вашего подтверждения и успешной генерации.
            Тариф без автопродления: он просто закончится, деньги сами не спишутся.
          </p>
        </motion.div>

        {/* ——— Докупить примерки ——— */}
        <motion.div {...rise(1)} className="relative mt-10 border border-white/10 p-6" data-testid="pack-buy-block">
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">
              Докупить примерки
            </p>
            <p className="font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
              {PACK_PRICE} ₽ за примерку
            </p>
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            <div className="flex items-center border border-white/15">
              <button
                onClick={() => setPackQty((q) => Math.max(1, q - 1))}
                className="flex h-11 w-11 items-center justify-center text-[#A3A3A3] transition-colors hover:text-[#C2A878]"
                aria-label="Меньше"
                data-testid="pack-minus"
              >
                <Minus size={14} strokeWidth={1.5} />
              </button>
              <span className="min-w-12 text-center font-cormorant text-2xl font-light text-[#F4F4F0]" data-testid="pack-qty">
                {packQty}
              </span>
              <button
                onClick={() => setPackQty((q) => Math.min(500, q + 1))}
                className="flex h-11 w-11 items-center justify-center text-[#A3A3A3] transition-colors hover:text-[#C2A878]"
                aria-label="Больше"
                data-testid="pack-plus"
              >
                <Plus size={14} strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-cormorant text-3xl font-light text-[#C2A878]" data-testid="pack-total">
              {packQty * PACK_PRICE} ₽
            </p>
          </div>
          <button
            onClick={buyPack}
            disabled={!!paying}
            className={`${btnGoldSolid} mt-5`}
            data-testid="pack-buy-button"
          >
            {paying === "pack" ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Создаём платёж
              </>
            ) : (
              `Купить ${packQty} ${pluralTryons(packQty)}`
            )}
          </button>
        </motion.div>

        {/* ——— Тарифы ——— */}
        <div className="mt-10 space-y-5" data-testid="account-plans">
          {plans.map((p, i) => {
            const current = sub?.active && sub.plan_id === p.id;
            const yearly = p.months >= 12;
            return (
              <motion.div
                key={p.id}
                {...rise(i)}
                className={`relative overflow-hidden p-7 transition-all duration-500 ${
                  p.is_pro
                    ? "border border-[#C2A878]/50 bg-gradient-to-b from-[#C2A878]/[0.07] to-transparent hover:shadow-[0_0_70px_rgba(194,168,120,0.10)]"
                    : "border border-white/10 hover:border-white/25"
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.is_pro && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-20"
                    style={{ background: "radial-gradient(circle, #C2A878 0%, transparent 70%)" }}
                  />
                )}
                <div className="flex items-center justify-between gap-3">
                  <span className="font-jetbrains text-[11px] tracking-[0.25em] text-[#C2A878]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#525252]">
                    {p.period}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <p className={`font-cormorant text-4xl font-light ${p.is_pro ? "text-[#C2A878]" : "text-[#F4F4F0]"}`}>
                    {p.title}
                  </p>
                  {p.is_pro && (
                    <span className="border border-[#C2A878]/50 px-2 py-0.5 font-jetbrains text-[8px] uppercase tracking-[0.25em] text-[#C2A878]">
                      {yearly ? "2 месяца в подарок" : "Флагман"}
                    </span>
                  )}
                </div>
                <p className="mt-2 font-cormorant text-5xl font-light leading-none text-[#F4F4F0]">
                  {p.price_rub}
                  <span className="ml-1 align-baseline font-cormorant text-2xl italic text-[#A3A3A3]">₽</span>
                </p>
                <ul className="mt-6 space-y-2.5 border-t border-white/5 pt-5">
                  {planFeatures(p).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-[#A3A3A3]">
                      <Check size={12} strokeWidth={2} className={`mt-1 shrink-0 ${p.is_pro ? "text-[#C2A878]" : "text-[#525252]"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => pay(p)}
                  disabled={!!paying || current || isDowngrade(p)}
                  className={`${p.is_pro ? btnGoldSolid : btnGhost} mt-7 ${current || isDowngrade(p) ? "border-white/5 bg-transparent text-[#525252] hover:border-white/5 hover:bg-transparent hover:text-[#525252]" : ""}`}
                  data-testid={`plan-buy-${p.id}`}
                >
                  {paying === p.id ? <Loader2 size={13} className="animate-spin" /> : null}
                  {current
                    ? "Ваш тариф"
                    : isDowngrade(p)
                      ? `«Про» действует до ${fmtUntil(sub.until)}`
                      : `Оформить · ${p.price_rub}₽`}
                </button>
                {isDowngrade(p) && (
                  <p className="mt-3 text-center font-jetbrains text-[9px] uppercase tracking-[0.15em] text-[#525252]" data-testid={`plan-downgrade-note-${p.id}`}>
                    Пока действует «Про» — доступны продление «Про» и пакеты примерок
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="mt-8 font-jetbrains text-[10px] uppercase leading-relaxed tracking-[0.15em] text-[#525252]" data-testid="tariffs-payment-note">
          Оплата картой (Visa, Mastercard, «Мир») или СБП в защищённой форме CloudPayments · доступ открывается автоматически сразу после оплаты · автопродления нет
        </p>
        <p className="mt-4 font-jetbrains text-[10px] uppercase leading-relaxed tracking-[0.15em] text-[#525252]">
          Оформляя тариф, вы принимаете{" "}
          <Link
            to="/offer"
            className="text-[#A3A3A3] underline underline-offset-2 transition-colors duration-300 hover:text-[#C2A878]"
            data-testid="tariffs-offer-link"
          >
            публичную оферту
          </Link>
        </p>
      </main>
    </div>
  );
}
